import { ChevronRightIcon } from "@chakra-ui/icons";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	Button,
} from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import CreateTicketForm, {
	type CreateTicketFormHandler,
} from "../components/forms/CreateTicketForm";
import PrimaryButton from "../components/PrimaryButton";
import { useNavigate } from "react-router-dom";
import { useCallback, useRef } from "react";
import { useMutation, useQueryClient } from "react-query";
import { createTicket } from "../mutations/ticket.mutations";
import { useSimpleToast } from "../hooks/toast.hooks";
import { useQueryParams } from "../hooks/utils.hooks";
import { useConnectedUser } from "../hooks/user.hooks";
import { useCenters } from "../hooks/centers.hooks";
import type { Ticket } from "../types/ticket.types";

function TicketingCreatePage() {
	const { commandId, next } = useQueryParams();
	const user = useConnectedUser();
	const { center } = useCenters();
	const navigate = useNavigate();
	const formRef = useRef<CreateTicketFormHandler>(null);
	const queryClient = useQueryClient();
	const toast = useSimpleToast();
	const { mutate: mutateCreateTicket, isLoading: createTicketIsLoading } =
		useMutation(createTicket, {
			onSuccess: (data: Ticket) => {
				queryClient.invalidateQueries(["command", "tickets"]);
				toast.success("Ticket", "Le ticket a été créé");
				if (typeof next === "string") {
					navigate(next);
					return;
				}
				navigate(`/tickets/${data.id}`);
				return;
			},
			onError: () => {
				toast.error(
					"Erreur",
					"Vérifiez que toutes les informations sont correctes",
				);
			},
		});
	const handleSubmit = useCallback(async () => {
		if (createTicketIsLoading) {
			return;
		}
		if (formRef.current) {
			const valid = await formRef.current.valid();
			if (valid) {
				const values = formRef.current.values();
				if (user?.isSuperAdmin) {
					mutateCreateTicket({
						ticketLabelId: values.ticketLabelId,
						commandId: values.commandId,
						description: values.description,
						priority: values.priority,
						centerId: undefined,
						files: Array.from(values.files as File[]),
					});
				} else {
					mutateCreateTicket({
						ticketLabelId: values.ticketLabelId,
						commandId: values.commandId,
						centerId: center?.id,
						description: values.description,
						priority: values.priority,
						files: Array.from(values.files as File[]),
					});
				}
			}
		}
	}, [
		createTicketIsLoading,
		mutateCreateTicket,
		center?.id,
		user?.isSuperAdmin,
	]);
	return (
		<>
			<Breadcrumb
				spacing="8px"
				separator={<ChevronRightIcon color="gray.500" />}
			>
				<BreadcrumbItem>
					<BreadcrumbLink
						href="/tickets"
						color="gray.600"
						fontWeight={600}
						fontSize="14px"
					>
						Ticketing
					</BreadcrumbLink>
				</BreadcrumbItem>

				<BreadcrumbItem>
					<BreadcrumbLink
						href="#"
						color="brand.700"
						fontWeight={600}
						fontSize="14px"
					>
						Nouveau ticket
					</BreadcrumbLink>
				</BreadcrumbItem>
			</Breadcrumb>

			<PageTitle>Nouveau ticket</PageTitle>

			<div className="vflex items-center">
				<div className="md:w-1/2">
					<CreateTicketForm
						ref={formRef}
						defaultValue={{
							commandId,
						}}
						editableDefault={false}
					></CreateTicketForm>
				</div>

				<div className="flex flex-col md:flex-row w-full md:w-1/2 gap-2 mt-4">
					<Button
						className="w-full"
						variant="outline"
						onClick={() => navigate("/tickets")}
					>
						Annuler
					</Button>
					<PrimaryButton
						onClick={handleSubmit}
						isDisabled={createTicketIsLoading}
					>
						Créer
					</PrimaryButton>
				</div>
			</div>
		</>
	);
}

export default TicketingCreatePage;
